import api from 'api/client';
import { ENDPOINTS } from 'api/endpoints';
import { getDefaultLayout } from 'config/defaultLayouts';
import {
  FeatureFlags,
  HeaderStyle,
  NavigationConfig,
  PageLayout,
  ThemeConfig,
  ThemeContactConfig,
  ThemeCookiesConfig,
  NavItem,
} from 'types/storefront';

const defaultFeatures: FeatureFlags = {
  promotionsEnabled: true,
  brandsEnabled: true,
  b2bPriceListsEnabled: true,
};

const asString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
};

const asBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  return undefined;
};

const mapContact = (raw: unknown): ThemeContactConfig | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  const data = raw as Record<string, unknown>;
  const contact: ThemeContactConfig = {
    whatsapp: asString(data.whatsapp),
    phone: asString(data.phone),
    email: asString(data.email),
    address: asString(data.address),
    instagramUrl: asString(data.instagramUrl),
  };
  return Object.values(contact).some(Boolean) ? contact : undefined;
};

const mapCookies = (raw: unknown): ThemeCookiesConfig | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  const data = raw as Record<string, unknown>;
  const cookies: ThemeCookiesConfig = {
    enabled: asBoolean(data.enabled),
    message: asString(data.message),
    acceptLabel: asString(data.acceptLabel),
  };
  if (cookies.enabled === undefined && !cookies.message && !cookies.acceptLabel) {
    return undefined;
  }
  return cookies;
};

const mapHeaderStyle = (value: unknown): HeaderStyle | undefined => {
  if (value === 'classic' || value === 'branded') return value;
  return undefined;
};

export const mapThemeResponse = (data: Record<string, unknown>): ThemeConfig => ({
  logoUrl: asString(data.logoUrl),
  faviconUrl: asString(data.faviconUrl),
  primaryColor: asString(data.primaryColor),
  secondaryColor: asString(data.secondaryColor),
  defaultColorMode: (data.defaultColorMode ?? data.paletteMode) as 'light' | 'dark' | undefined,
  siteName: asString(data.siteName),
  fontFamily: asString(data.fontFamily),
  headerStyle: mapHeaderStyle(data.headerStyle) ?? 'classic',
  contact: mapContact(data.contact),
  cookies: mapCookies(data.cookies),
});

const storefrontService = {
  getTheme: async (): Promise<ThemeConfig> => {
    try {
      const response = await api.get<Record<string, unknown>>(ENDPOINTS.STOREFRONT_THEME);
      return mapThemeResponse(response.data ?? {});
    } catch {
      return { headerStyle: 'classic' };
    }
  },

  getLayout: async (page: string): Promise<PageLayout> => {
    try {
      const response = await api.get<PageLayout>(ENDPOINTS.STOREFRONT_LAYOUT, { params: { page } });
      return response.data;
    } catch {
      return getDefaultLayout(page) ?? { page, sections: [] };
    }
  },

  getNavigation: async (): Promise<NavigationConfig> => {
    try {
      const response = await api.get<{ header?: Array<Record<string, unknown>>; footer?: Array<Record<string, unknown>> }>(
        ENDPOINTS.STOREFRONT_NAVIGATION,
      );
      const mapItems = (items: Array<Record<string, unknown>> = []): NavItem[] =>
        items.map((item) => ({
          label: String(item.label ?? item.Label ?? '').trim() || undefined,
          labelKey: String(item.labelKey ?? item.label ?? item.Label ?? '').trim() || 'nav.item',
          url: String(item.url ?? item.Url ?? '/'),
          children: item.children ? mapItems(item.children as Array<Record<string, unknown>>) : undefined,
        }));
      return {
        header: mapItems(response.data.header),
        footer: mapItems(response.data.footer),
      };
    } catch {
      return { header: [], footer: [] };
    }
  },

  getFeatures: async (): Promise<FeatureFlags> => {
    try {
      const response = await api.get<FeatureFlags>(ENDPOINTS.STOREFRONT_FEATURES);
      return response.data;
    } catch {
      return defaultFeatures;
    }
  },
};

export default storefrontService;
